import { Outlet, useParams, useLocation } from 'react-router-dom';
import ConsoleLayout from '@/components/layout/ConsoleLayout';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { RouteParams } from '@/router/type';
import { useGetServerlessProjectById } from '@/service/serverless-gpu/hook';
import DataNotFound from '@/components/ui/not-found/data-not-found';
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useMemo, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { NavItems } from '@/components/layout/navigation/configNav';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import ProviderServerlessToken from '../provider-token';
import BadgeProjectStatus from '../components/BadgeProjectStatus';
import { StatusServerlessProject } from '@/service/serverless-gpu/type';
import HeaderLayout from './components/header-layout';
import useConsoleLayoutConfig from '@/hook/useConsoleLayoutConfig';
import { SparkleIcon } from 'lucide-react';

const ServerlessDetailLayout = () => {
  const { workspaceId } = useParamWorkspaceId();
  const { projectId } = useParams<RouteParams>();
  const location = useLocation();
  const isOnboarding = location.pathname.endsWith('/onboarding');
  const {
    data: dataProject,
    isLoading,
    isError,
  } = useGetServerlessProjectById({
    project_id: projectId!,
  });
  const data = dataProject?.project;

  const loadingBarRef = useRef<LoadingBarRef>(null);
  const isActive = data?.status === StatusServerlessProject.Active;
  const isBlueprint = data?.project_name?.includes('[Blueprint]');

  const Layout = ({ children }: { children: React.ReactNode }) => (
    <ServerlessDetailConsoleLayout isActive={isActive} isBlueprint={isBlueprint}>
      {children}
    </ServerlessDetailConsoleLayout>
  );

  useEffect(() => {
    loadingBarRef.current?.continuousStart();
  }, []);

  if (isLoading) {
    return (
      <Layout>
        <LoadingBar color="#3c92e7" ref={loadingBarRef} />
        <div className="container mt-8">
          <Skeleton className="h-4 w-48" />
          <div className="mt-6 flex flex-col gap-4">
            <Skeleton className="h-4 w-80" />
            <Skeleton className="h-4 w-80" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!data?.project_id || isError) {
    return (
      <Layout>
        <DataNotFound
          goBackHref={PATH_PAGE.dashboard.serverlessGPU.root(workspaceId)}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      {!isOnboarding && (
        <div className="container mt-8">
          <HeaderLayout
            projectId={data.project_id}
            projectName={data.project_name!}
            status={data.status}
          />
        </div>
      )}
      <div>
        <Outlet />
      </div>
    </Layout>
  );
};

export default ServerlessDetailLayout;

const ServerlessDetailConsoleLayout = ({
  children,
  isActive,
  isBlueprint
}: {
  children: React.ReactNode;
  isActive?: boolean;
  isBlueprint?: boolean;
}) => {
  const { workspaceId } = useParamWorkspaceId();
  const { projectId } = useParams<RouteParams>();

  const navItems: NavItems = [
    {
      href: PATH_PAGE.dashboard.serverlessGPU.overview(workspaceId),
      label: 'All projects',
      strictMatch: true,
      separator: true,
      icon: <ArrowLeftIcon />,
    },
    {
      href: PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
        .overview,
      label: 'Overview',
      strictMatch: true,
    },
    ...(isActive
      ? [
          {
            href: PATH_PAGE.dashboard.serverlessGPU.byId(
              workspaceId,
              projectId!
            ).deploy,
            label: 'Deploy',
            strictMatch: true,
          },
          {
            href: PATH_PAGE.dashboard.serverlessGPU.byId(
              workspaceId,
              projectId!
            ).develop,
            label: 'Develop',
            strictMatch: true,
          },
          {
            href: PATH_PAGE.dashboard.serverlessGPU.byId(
              workspaceId,
              projectId!
            ).storage,
            label: 'Storage',
            strictMatch: true,
          },
        ]
      : []),
    {
      href: PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
        .task,
      label: 'Tasks',
      strictMatch: true,
    },
    ...(isActive
      ? [
          {
            href: isBlueprint
              ? PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
                  .playgroundBlueprint
              : PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
                  .playground,
            label: 'Playground',
            strictMatch: true,
            // icon: <SparkleIcon className='h-4 w-4'/>
          },
        ]
      : []),
    {
      href: PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
        .activity,
      label: 'Activity',
      strictMatch: true,
      separator: isActive ? false : true,
    },
    ...(isActive
      ? [
          {
            href: PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
              .setting,
            label: 'Setting',
            strictMatch: true,
            separator: true,
          },
        ]
      : []),
    {
      href: PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId, projectId!)
        .onboarding,
      label: 'Quickstart',
      strictMatch: true,
    },
  ];

  const navbarConfig = useMemo(
    () => ({
      title: 'Serverless GPU',
      navbar: navItems,
    }),
    [isActive, workspaceId, projectId]
  );
  useConsoleLayoutConfig({
    navConfig: navbarConfig,
  });

  return <ProviderServerlessToken>{children}</ProviderServerlessToken>;
};
